import { useLang } from '../context/LanguageContext';
import { toEmbedVideoUrl, isDirectVideoFile, resolveAdVideoSrc } from '../utils/adMedia.js';
import { pickTeEn } from '../utils/localizedContent.js';
import './AdUnit.css';

import { API_URL as API } from '../config/env.js';

function mediaSrc(ad) {
  if (!ad.image) return '';
  return ad.image.startsWith('http') ? ad.image : `${API}${ad.image}`;
}

function BannerAd({ ad, title, content, buttonLabel, sponsor, active = true }) {
  const isVideo = ad.mediaType === 'video' && ad.videoUrl;
  const videoSrc = isVideo ? resolveAdVideoSrc(ad.videoUrl, API) : '';
  const embedSrc = isVideo && !isDirectVideoFile(videoSrc) ? toEmbedVideoUrl(ad.videoUrl) : '';
  const directVideo = isVideo && isDirectVideoFile(videoSrc);
  const hasVisual = (ad.mediaType === 'image' && ad.image) || isVideo;
  const hasCta = Boolean(buttonLabel && ad.buttonUrl);

  const inner = (
    <>
      {hasVisual && (
        <div className="ad-banner__visual" aria-hidden={!title}>
          <div className="ad-banner__visual-frame">
            {ad.mediaType === 'image' && ad.image && (
              <img
                src={mediaSrc(ad)}
                alt=""
                loading={active ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={active ? 'high' : 'auto'}
              />
            )}
            {isVideo && embedSrc && !directVideo && active && (
              <iframe
                src={embedSrc}
                title={title || 'Video ad'}
                loading="lazy"
                allow="encrypted-media; picture-in-picture"
                allowFullScreen
              />
            )}
            {isVideo && embedSrc && !directVideo && !active && (
              <div className="ad-banner__video-idle" aria-hidden />
            )}
            {isVideo && directVideo && (
              <video
                src={active ? videoSrc : undefined}
                controls
                playsInline
                muted
                preload={active ? 'metadata' : 'none'}
              />
            )}
          </div>
        </div>
      )}
      <div className={`ad-banner__content ${hasVisual ? '' : 'ad-banner__content--full'}`}>
        <span className="ad-banner__sponsor">{sponsor}</span>
        {title && <h3 className="ad-banner__title">{title}</h3>}
        {content && <p className="ad-banner__text">{content}</p>}
        {hasCta && (
          <span className="ad-banner__cta ad-banner__cta--inline">
            {buttonLabel}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </div>
      {hasCta && (
        <div className="ad-banner__action">
          <span className="ad-banner__cta">
            {buttonLabel}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      )}
    </>
  );

  const className = `ad-banner${hasVisual ? '' : ' ad-banner--text-only'}${hasCta ? ' ad-banner--has-cta' : ''}`;

  if (ad.buttonUrl) {
    return (
      <a
        href={ad.buttonUrl}
        className={className}
        target="_blank"
        rel="noopener noreferrer sponsored"
      >
        <div className="ad-banner__inner">{inner}</div>
      </a>
    );
  }

  return (
    <article className={className}>
      <div className="ad-banner__inner">{inner}</div>
    </article>
  );
}

export default function AdUnit({ ad, variant = 'sidebar', active = true }) {
  const { lang, t } = useLang();
  if (!ad) return null;

  const title = pickTeEn(lang, ad.title, ad.titleEn);
  const content = pickTeEn(lang, ad.content, ad.contentEn);
  const buttonLabel = pickTeEn(lang, ad.buttonLabel, ad.buttonLabelEn);
  const sponsor = ad.sponsorLabel || t.adSponsored || 'Sponsored';

  if (variant === 'banner') {
    return (
      <BannerAd
        ad={ad}
        title={title}
        content={content}
        buttonLabel={buttonLabel}
        sponsor={sponsor}
        active={active}
      />
    );
  }

  const isVideo = ad.mediaType === 'video' && ad.videoUrl;
  const videoSrc = isVideo ? resolveAdVideoSrc(ad.videoUrl, API) : '';
  const embedSrc = isVideo && !isDirectVideoFile(videoSrc) ? toEmbedVideoUrl(ad.videoUrl) : '';
  const directVideo = isVideo && isDirectVideoFile(videoSrc);

  const media = (
    <div className="ad-unit__media">
      {ad.mediaType === 'image' && ad.image && (
        <img src={mediaSrc(ad)} alt={title || 'Advertisement'} loading="lazy" decoding="async" />
      )}
      {isVideo && embedSrc && !directVideo && (
        <iframe
          src={embedSrc}
          title={title || 'Video ad'}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      )}
      {isVideo && directVideo && (
        <video src={videoSrc} controls playsInline muted preload="metadata" />
      )}
      <span className="ad-unit__sponsored">{sponsor}</span>
    </div>
  );

  const body = (title || content || buttonLabel) && (
    <div className="ad-unit__body">
      {title && <h4 className="ad-unit__title">{title}</h4>}
      {content && <p className="ad-unit__text">{content}</p>}
      {buttonLabel && ad.buttonUrl && (
        <a
          href={ad.buttonUrl}
          className="ad-unit__cta"
          target="_blank"
          rel="noopener noreferrer sponsored"
        >
          {buttonLabel}
          <span aria-hidden>→</span>
        </a>
      )}
    </div>
  );

  return (
    <article className="ad-unit ad-unit--sidebar">
      {media}
      {body}
    </article>
  );
}
